import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import { useHistory } from "react-router-dom";
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import FullWidthButton, { FullWidthButtonProps, FullWidthButtonSize } from '../common/Buttons/FullWidthButton';
import Rating, { RatingProps } from './Rating';
import { SendRatingResponse } from '../../models/postcheckout/SendRatingResponse';
import { SendRatingRequest } from '../../models/postcheckout/SendRatingRequest';
import { RatingLevel } from '../../models/postcheckout/RatingLevel';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import {  String_System_Done, String_CheckoutComplete_ThankYouMessage, String_CheckoutComplete_RatingInstruction,
     String_System_GeneralError, String_System_ConnectionError } from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import { ElementDimensions } from '../../styles/ElementDimensions';
import Loader from '../common/Loader';
import { CheckSummary } from '../../models/common/CheckSummary';
import SignalR, { CheckMessageType, CheckNotificationType, OnNotificationReceivedHandler } from '../common/SignalR';
import { FeedbackType } from '../../models/common/FeedbackType';

const useStyles = makeStyles({
    details: {
        position: 'absolute',
        top: (ElementDimensions.margin10 + ElementDimensions.appBarHeight) + 'px',
        bottom: ElementDimensions.footerBarHeight + 'px',
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    detailsLandscape: {
        position: 'absolute',
        top: ElementDimensions.margin5 + 'px',
        bottom: ElementDimensions.footerBarHeightLandscape + 'px',
        left: 'calc(100vw *.3)',
        width: 'calc(100vw *.7)',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    gifContainer: {
        width: '50%',
        position: 'relative', 
        paddingBottom: 'calc(40% + 52px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    gifOverlay: {
        width: '100%',
        height: '100%',
        backgroundColor: 'transparent',
        position: 'absolute',
        top: '0',
        left: '0',
        paddingBottom: 'calc(40% + 52px)',
        marginLeft: 'auto',
        marginRight: 'auto',
        zIndex: 9999,
    },
    gifIFrame: {
        position: 'absolute',
        top: '0',
        left: '0',
        zIndex: 1,
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'calc(25vw - .2em) calc(25vw - .2em) calc(25vw - .2em) calc(25vw - .2em)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    gridContainer: {
        textAlign: 'center',
        display: 'flex',
        marginLeft: ElementDimensions.margin5 +  'px',
        marginRight: ElementDimensions.margin5 +  'px',
        marginTop: ElementDimensions.margin20 +  'px',
    },
    gridLandscape: {
        display: 'grid',
        gridTemplateColumns: 'calc((100vw - (100vh *.7)) * .25) calc((100vw - (100vh *.7)) * .25) calc((100vw - (100vh *.7)) * .25) calc((100vw - (100vh *.7)) * .25)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    icon: {
        height: '30px',
        width: '30px',
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    logoContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        textAlign: 'center',
        paddingTop: ElementDimensions.padding10 + 'px',
        paddingBottom: ElementDimensions.padding10 + 'px',
    },
    ratingInstruction: {
        paddingLeft: ElementDimensions.padding5 +  'px',
        paddingRight: ElementDimensions.padding5 +  'px',
    },
    ratingText: {
        fontSize: '.8em',
    },
    remainingCheckNumber: {
        fontWeight: 'bold',
    },
    remainingCheckText: {
        marginTop: ElementDimensions.margin10 +  'px',
        paddingLeft: ElementDimensions.padding10 +  'px',
        paddingRight: ElementDimensions.padding10 +  'px',
    },
    root: {
        marginTop: ElementDimensions.margin10 + 'px',
        height: '100%',
        overflow: 'hidden',
        textAlign: 'center',
    },
    rootLandscape: {
        marginTop: ElementDimensions.margin5 + 'px',
        textAlign: 'center',
    },
    thankYouText: {
        paddingLeft: ElementDimensions.padding10 +  'px',
        paddingRight: ElementDimensions.padding10 +  'px',
    },
});

const CheckOutComplete = (props: any) => {
    const isPortrait = true;
    let history = useHistory();
    const [ratingValue, setRatingValue] = React.useState<RatingLevel | null>(null);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [gifIndex, setGifIndex] = React.useState<number>(-1);
    const [buttonClickLock, setButtonClickLock] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(true);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const [checks, setChecks] = React.useState<CheckSummary[] | null>(null);
    const [signalRInitialized, setSignalRInitialized] = React.useState<boolean>(false);
    const [signalRNotificationHandler, setSignalRNotificationHandler] = React.useState<OnNotificationReceivedHandler | null>(null);
    const [signalRLoad, setSignalRLoad] = React.useState<boolean>(false);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    let paymentId: number = history.location.state.paymentId;
    let gifs: string[] = history.location.state.gifs;

    const signalRNotificationReceivedHandler = (type: CheckNotificationType, amount: number) => {
        if (type === CheckNotificationType.AmountClaimed) {

        }
        else if (type === CheckNotificationType.PaymentCompleted) {

        }
        else if (type === CheckNotificationType.CheckClosed) {
            
        }
        else if (type === CheckNotificationType.ClaimExpired) {
            
        }
    }

    React.useEffect(() => {
        setSignalRNotificationHandler(signalRNotificationReceivedHandler);
        setSignalRLoad(true);

        if (gifIndex === -1) {
            setGifIndex(Math.floor(Math.random() * Math.floor(gifs.length)));
        }
        return () => {
            setSignalRNotificationHandler(null);
        };
    }, [gifIndex, signalRInitialized, signalRLoad]);

    const handleOkButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setSignalRNotificationHandler(null);
           history.push({
                pathname: '/postcheckout/review',
                state: { 
                    paymentId: paymentId,
                }
            }); 
    }

    const sendRating = (cancelTokenSource: CancelTokenSource) => {
        const request: SendRatingRequest = {
            user_id: userState.state.userId!!,
            check_please_payment_id: paymentId,
            rating: ratingValue,
        };

        axios.post(process.env.REACT_APP_CHECKOUT_API_URL + '/postcheckout/rating/', request, { cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as SendRatingResponse;
            if (r.success === true) {
                const state: CheckOutState = checkOutState.state;
                state.sentRating = true;
                checkOutState.setState(state);

                setSignalRNotificationHandler(null);
            }
            else {                
                setButtonClickLock(false);
                setLoaded(true);
            }
        })
        .catch(function(error) {
            setButtonClickLock(false);
            setLoaded(true);
        })
        .finally (function(){
            history.push({
                pathname: '/postcheckout/review',
                state: { 
                    paymentId: paymentId,
                }
            }); 
        });
    }

    const buttonProps: FullWidthButtonProps = {
        disabled: false,
        label: String_System_Done.find(s => s.key === userState.state.locale)!!.value,
        alternateColor: false,
        size: FullWidthButtonSize.Large,
        onClickHandler: handleOkButtonClick,
    }

    const handleRatingChange = (value: RatingLevel) => {
        setRatingValue(value);
    }

    const ratingProps: RatingProps = {
        onChangeHandler: handleRatingChange,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        handleOkButtonClick(event);
        setErrorDialogOpen(false);

        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        sendRating(source);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: true,
        onCloseHandler: handleErrorDialogClose,
    }

    const getGif = () => {
        console.log("getGif - " + gifs[gifIndex - 1]);
        let url = gifs[gifIndex - 1];

        if ((url === null || url === undefined) || url.trim() == '') {
            setGifIndex(Math.floor(Math.random() * Math.floor(gifs.length)));
        }

        return gifs[gifIndex - 1];
    }

    const gifClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    }

    const signalRInitializedHandler = () => {
        setSignalRInitialized(true);
    }

    return (
        <div className={clsx(classes.root, {
            [classes.rootLandscape]: !isPortrait,
        })}>

            {!loaded &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            {loaded &&
                <div>
                    <div className={isPortrait === true ? classes.details : classes.detailsLandscape}>
                        <div>
                            <Typography variant='h6' className={classes.thankYouText}>
                                {String_CheckoutComplete_ThankYouMessage.find(s => s.key === userState.state.locale)!!.value}
                            </Typography>
                        </div>
                        <div>

                            <div className={classes.gifContainer}>
                                <div className={classes.gifOverlay} onClick={gifClick}>
                                </div>
                                <iframe src={getGif()} width='100%' height='100%' 
                                className={classes.gifIFrame} frameBorder='0'/>
                            </div>
                            
                        </div>
                    </div>
                    
                    <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                        <FullWidthButton 
                            disabled={buttonProps.disabled}
                            label={buttonProps.label}
                            alternateColor={buttonProps.alternateColor}
                            size={buttonProps.size}
                            onClickHandler={buttonProps.onClickHandler}/>
                    </div>
                </div>
            }
            
            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            {checkOutState.state.checks !== null && 
                <SignalR 
                    oldChecks={null}
                    messageType={CheckMessageType.Subscribe}
                    load={signalRLoad}
                    refreshCheck={false}
                    onNotificationReceived={signalRNotificationHandler}
                    onInitialized={signalRInitializedHandler} />
            }

        </div>
    );
};

export default CheckOutComplete;